import { useSuspenseQuery } from '@apollo/client';
import { useMemo } from 'react';

import {
  ClickReasonsEnum,
  EntityPageFocusType,
  EntityPagePublicationStatus,
} from '@xing-com/crate-common-graphql-types';
import { useNavigate } from '@xing-com/crate-core-router';
import {
  NEW_COMPANY_URL,
  PAGES_PATH,
} from '@xing-com/crate-entity-pages-entity-pages-shared-urls';
import { useExperiment } from '@xing-com/hub';

import { PageContextType } from '../../context/entity-page-context/entity-page-context';
import { EntityPageDocument } from '../../graphql/queries/entity-page-query.gql-types';
import { useIsWebview } from '../../hooks/use-is-webview/use-is-webview';

type EntityPageHookResult =
  | {
      pageContext: PageContextType;
      redirectTo: undefined;
      errorCode: undefined;
    }
  | {
      pageContext: undefined;
      redirectTo: string;
      errorCode: undefined;
    }
  | {
      pageContext: undefined;
      redirectTo: undefined;
      errorCode: 404 | 500 | 503;
    };

type Options = {
  pageSlug: string;
};
export const useEntityPage = ({ pageSlug }: Options): EntityPageHookResult => {
  const isWebview = useIsWebview();
  const navigate = useNavigate();

  const { data, error } = useSuspenseQuery(EntityPageDocument, {
    variables: {
      id: pageSlug,
      socialProofClickReasonsKey:
        ClickReasonsEnum.CrWebPublisherSocialProofHeader,
      shouldHideKununuData: useExperiment('ABACUS-518') === 'B',
    },
    errorPolicy: 'all',
  });

  return useMemo((): EntityPageHookResult => {
    const entityPage = data?.entityPageEX;

    if (error || entityPage?.__typename !== 'EntityPage') {
      switch (entityPage?.__typename) {
        case 'EntityPageError':
          return {
            pageContext: undefined,
            redirectTo: undefined,
            errorCode: entityPage.errorCode === 10404 ? 404 : 503,
          };

        case 'EntityPageMoved':
          return {
            pageContext: undefined,
            redirectTo: PAGES_PATH(entityPage.slug),
            errorCode: undefined,
          };

        default:
          return {
            pageContext: undefined,
            redirectTo: undefined,
            errorCode: 503,
          };
      }
    }

    if (entityPage.publicationStatus === 'CREATED') {
      return {
        pageContext: undefined,
        redirectTo: NEW_COMPANY_URL,
        errorCode: undefined,
      };
    }

    const headerModule =
      data?.headerModule?.__typename === 'EntityPageHeaderModule'
        ? data.headerModule
        : undefined;

    const isFollowingPage =
      entityPage.userPageContext?.userInteractions?.__typename ===
        'EntityPageUserInteractionFollow' &&
      entityPage.userPageContext.userInteractions.followState?.isFollowing;

    const isEditor = !!entityPage.userPageContext?.permissions?.canEdit;
    const isAdmin = !!entityPage.userPageContext?.permissions?.canAdmin;

    const focusType = entityPage.focusType;
    const isCompanyPage = focusType === EntityPageFocusType.Company;
    const isGroupPage = focusType === EntityPageFocusType.GroupPage;
    const isPremiumPage = focusType === EntityPageFocusType.PremiumPage;
    const isIndustryPage = focusType === EntityPageFocusType.IndustryPage;
    const isPublisherPage = focusType === EntityPageFocusType.Publisher;
    const isTopicPage = focusType === EntityPageFocusType.TopicPage;
    const isInsiderPage = focusType === EntityPageFocusType.Insider;

    const publicationStatus = entityPage.publicationStatus;
    const isPageDraft = publicationStatus === EntityPagePublicationStatus.Draft;
    const isPagePublished =
      publicationStatus === EntityPagePublicationStatus.Published;
    const contentTopicCollection =
      entityPage.entity?.__typename === 'ContentInsiderPage'
        ? entityPage.entity?.topics?.collection
        : undefined;
    const insiderPageName =
      entityPage.entity?.__typename === 'ContentInsiderPage'
        ? entityPage.entity?.xingId?.pageName
        : undefined;

    return {
      pageContext: {
        globalId: entityPage.globalId,
        pageSlug: entityPage.slug,
        pageId: entityPage.id,
        focusType: focusType as EntityPageFocusType,
        isCompanyPage,
        isPremiumPage,
        isGroupPage,
        isIndustryPage,
        isPublisherPage,
        isTopicPage,
        isInsiderPage,
        pageSlogan: entityPage.slogan ?? '',
        isFollowingPage: isFollowingPage,
        companyId: entityPage.context?.companyId,
        cppId: entityPage.context?.cppId,
        socialProof: entityPage.userPageContext?.socialProof,
        basePath: `/pages${isWebview ? '/webview' : ''}`,
        contractType: entityPage.contract?.type,
        publicationStatus,
        isPageDraft,
        isPagePublished,
        visibility: entityPage.visibility,
        url: entityPage.links?.self,
        pageTitle: entityPage.title,
        upsellActive:
          entityPage.contract?.type === 'FREE' &&
          (isAdmin || isEditor) &&
          entityPage.focusType === EntityPageFocusType.Company,
        logo: entityPage.logoImage?.[0].url ?? '',
        isEditor,
        isAdmin,
        contentTopicCollection,
        insiderPageName,
        contentPageGlobalId: entityPage.entity?.globalId,
        headerModule,

        goBackUrl: entityPage.globalId
          ? () => navigate(-1)
          : () => navigate(PAGES_PATH(entityPage.slug)),
      },
      errorCode: undefined,
      redirectTo: undefined,
    };
  }, [data, error, isWebview, navigate]);
};
